import dayjs from "dayjs";
import { useState } from "react";
import { Actions, Features, useAnalyticsContext } from "./analytics-context";
import { useMetriportApi } from "./useMetriportApi";
import useMetriportToast from "./useMetriportToast";
import { isAxiosError } from "axios";
import { capture } from "../../../shared/capture";
import { handleNetworkErrorWithTryAgainToast } from "../../error/utils";
import { axiosErrorCodes } from "../../shared/constants";

const EXPLAIN_TOAST_DURATION = dayjs.duration(15, "second").asMilliseconds();

export type UsePdfViewer = {
  isLoading: boolean;
  pdfUrl: string | undefined;
  error: string | undefined;
  fetchPdfUrl: (docId: string, docFilename: string) => Promise<void>;
  clearPdfUrl: () => void;
};

export function usePdfViewer(): UsePdfViewer {
  const toast = useMetriportToast();
  const metriportApi = useMetriportApi();
  const Analytics = useAnalyticsContext();

  const [isLoading, setIsLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  async function fetchPdfUrl(docId: string, docFilename: string): Promise<void> {
    Analytics.emit(Actions.download, Features.document);
    setIsLoading(true);
    setError(undefined);
    setPdfUrl(undefined);

    toast.info({
      title: "Hold tight... This might take up to 30 seconds.",
      duration: EXPLAIN_TOAST_DURATION,
    });

    try {
      const { url } = await metriportApi.getDocumentUrl(docFilename, "pdf");
      setPdfUrl(url);
    } catch (err) {
      const { handled } = handleTimeoutOrNetworkError(err, toast);
      if (!handled) {
        const msg = "Failed to load PDF document.";
        capture.error(err, {
          extra: { docId, context: `patient.file.pdf-viewer`, error: err },
        });
        toast.error({ title: msg });
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  }

  function clearPdfUrl() {
    setPdfUrl(undefined);
    setError(undefined);
  }

  return { isLoading, pdfUrl, error, fetchPdfUrl, clearPdfUrl };
}

function handleTimeoutOrNetworkError(
  error: unknown,
  toast: ReturnType<typeof useMetriportToast>
): { handled: boolean } {
  if (!isAxiosError(error)) return { handled: false };
  if (error.code === axiosErrorCodes.TIMEOUT) {
    toast.error({
      title:
        "The document may be too large to load, please use the API to get the contents or try again. If the issue persists, please reach out to support@metriport.com.",
      duration: EXPLAIN_TOAST_DURATION,
    });
    return { handled: true };
  }
  return handleNetworkErrorWithTryAgainToast(error, toast);
}
